import { useState, useEffect } from 'react';
import { isColorDark, hexToRgba, resolveLinkProps } from '../utils';

function calcRemaining(target) {
    const diff = Math.max(0, new Date(target).getTime() - Date.now());
    return {
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
        expired: diff <= 0,
    };
}

export default function NotificationBarSection({ block = {}, primaryColor, secondaryColor, isPreview }) {
    const { config = {}, content = {} } = block;
    const [dismissed, setDismissed] = useState(false);
    const [mounted, setMounted] = useState(!!isPreview);

    const variant = config.variant || 'slim';
    const bg = config.bg_color || primaryColor;
    const textColor = config.text_color || (isColorDark(bg) ? '#ffffff' : '#111827');
    const dismissible = content.dismissible !== false;
    const storageKey = `notif_bar_${block.id || 'default'}_dismissed`;

    const [time, setTime] = useState(() => calcRemaining(content.countdown_end || config.countdown_end));

    useEffect(() => {
        if (isPreview) return;
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(storageKey)) {
            setDismissed(true);
            return;
        }
        setMounted(true);
    }, []);

    useEffect(() => {
        const target = content.countdown_end || config.countdown_end;
        if (variant !== 'with-countdown' || !target) return;
        const id = setInterval(() => setTime(calcRemaining(target)), 1000);
        return () => clearInterval(id);
    }, [variant, content.countdown_end, config.countdown_end]);

    if ((dismissed && !isPreview) || !content.message) return null;

    const handleDismiss = () => {
        setDismissed(true);
        if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(storageKey, '1');
    };

    const linkProps = content.link_url ? resolveLinkProps(content.link_url) : null;

    // Countdown inline display
    const countdownDisplay = variant === 'with-countdown' && !time.expired ? (
        <span className="inline-flex items-center gap-1 ml-3 font-mono text-sm font-bold tabular-nums" style={{ color: textColor }}>
            <span className="px-1.5 py-0.5 rounded" style={{ backgroundColor: hexToRgba(textColor, 0.15) }}>
                {String(time.days).padStart(2, '0')}
            </span>
            :
            <span className="px-1.5 py-0.5 rounded" style={{ backgroundColor: hexToRgba(textColor, 0.15) }}>
                {String(time.hours).padStart(2, '0')}
            </span>
            :
            <span className="px-1.5 py-0.5 rounded" style={{ backgroundColor: hexToRgba(textColor, 0.15) }}>
                {String(time.minutes).padStart(2, '0')}
            </span>
            :
            <span className="px-1.5 py-0.5 rounded" style={{ backgroundColor: hexToRgba(textColor, 0.15) }}>
                {String(time.seconds).padStart(2, '0')}
            </span>
        </span>
    ) : null;

    // Marquee variant content
    const messageContent = (
        <>
            <span>{content.message}</span>
            {content.link_text && linkProps && (
                <a {...linkProps} className="underline underline-offset-2 font-semibold ml-2 hover:opacity-80 transition-opacity" style={{ color: textColor }}>
                    {content.link_text}
                </a>
            )}
        </>
    );

    if (variant === 'marquee') {
        return (
            <div
                className={`${isPreview ? 'relative w-full' : `fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${mounted ? 'translate-y-0' : '-translate-y-full'}`} overflow-hidden`}
                style={{ backgroundColor: bg, height: '40px' }}
            >
                <div className="relative h-full flex items-center">
                    <div className="animate-marquee-notification whitespace-nowrap flex items-center gap-16 h-full">
                        {[0, 1, 2, 3, 4].map(n => (
                            <span key={n} className="inline-flex items-center gap-2 text-sm font-medium px-8" style={{ color: textColor }}>
                                {messageContent}
                            </span>
                        ))}
                    </div>
                </div>
                {dismissible && (
                    <button
                        onClick={handleDismiss}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full transition-colors hover:bg-white/20 z-10"
                        style={{ color: textColor }}
                    >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
                <style>{`
                    @keyframes marquee-notification {
                        0% { transform: translateX(0); }
                        100% { transform: translateX(-50%); }
                    }
                    .animate-marquee-notification {
                        animation: marquee-notification 20s linear infinite;
                    }
                `}</style>
            </div>
        );
    }

    // Slim (default) and with-countdown variants
    return (
        <div
            className={`${isPreview ? 'relative w-full' : `fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${mounted ? 'translate-y-0' : '-translate-y-full'}`}`}
            style={{ backgroundColor: bg }}
        >
            <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-2" style={{ height: '40px' }}>
                <p className="text-sm font-medium text-center truncate flex items-center" style={{ color: textColor }}>
                    <span>{content.message}</span>
                    {content.link_text && linkProps && (
                        <a {...linkProps} className="underline underline-offset-2 font-semibold ml-2 hover:opacity-80 transition-opacity" style={{ color: textColor }}>
                            {content.link_text}
                        </a>
                    )}
                    {countdownDisplay}
                </p>
                {dismissible && (
                    <button
                        onClick={handleDismiss}
                        className="shrink-0 p-1 rounded-full transition-colors hover:bg-white/20 ml-2"
                        style={{ color: textColor }}
                    >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
}
